import type { Assignment } from './types';

// How grade-book entries become a percentage: the same sums as the server's grade-average.ts (#97, #821), so the Grades
// tab, the Student Portal, report cards and the School Resources panels agree.
//
// Entries added here: points earned / available averaged by each entry's weight (weight 0 doesn't count). Connexus
// grades with their course's type weights: each type's points earned / available, averaged by the type weights, over
// the types that have a score; the overall is then the plain average of the subjects' grades, as Connexus shows it.

type Item = Pick<Assignment, 'subject' | 'pointsEarned' | 'pointsAvailable' | 'weight' | 'schoolType' | 'typeWeight'>;

const schoolWeighted = (items: Item[]) => items.some((a) => !!a.schoolType && (a.typeWeight ?? 0) > 0);

/** Whether an entry counts toward a grade: points available and a weight (its own, or its school type's). */
export const counted = (a: Item) => a.pointsAvailable > 0 && ((a.typeWeight ?? 0) > 0 || a.weight > 0);

function weightedPct(items: Item[]): number | null {
  const graded = items.filter((a) => a.pointsAvailable > 0 && a.weight > 0);
  const wsum = graded.reduce((s, a) => s + a.weight, 0);
  if (!wsum) return null;
  return (graded.reduce((s, a) => s + a.weight * (a.pointsEarned / a.pointsAvailable), 0) / wsum) * 100;
}

function schoolPct(items: Item[]): number | null {
  const types = new Map<string, { earned: number; available: number; weight: number }>();
  for (const a of items) {
    if (!a.schoolType || !(a.pointsAvailable > 0)) continue;
    const t = types.get(a.schoolType) ?? { earned: 0, available: 0, weight: 0 };
    t.earned += a.pointsEarned;
    t.available += a.pointsAvailable;
    t.weight = Math.max(t.weight, a.typeWeight ?? 0);
    types.set(a.schoolType, t);
  }
  let sum = 0;
  let wsum = 0;
  for (const t of types.values()) {
    if (t.weight <= 0) continue;
    sum += t.weight * (t.earned / t.available);
    wsum += t.weight;
  }
  return wsum ? (sum / wsum) * 100 : null;
}

/** One subject's grade, or null when nothing in it is scored. */
export const gradePct = (items: Item[]): number | null => (schoolWeighted(items) ? schoolPct(items) : weightedPct(items));

/** The grade over every subject: with Connexus grades, the average of the subjects' grades; otherwise weighted over every entry. */
export function overallPct(items: Item[]): number | null {
  if (!schoolWeighted(items)) return weightedPct(items);
  const pcts = [...new Set(items.map((a) => a.subject))].map((s) => gradePct(items.filter((a) => a.subject === s))).filter((p): p is number => p !== null);
  return pcts.length ? pcts.reduce((s, p) => s + p, 0) / pcts.length : null;
}
